/**
 * Step 2 do wizard ZTNA — Identidade.
 *
 * Campos:
 *   - slug (kebab-case validado).
 *   - display_name (livre).
 *   - environment (readonly, derivado do tenant Step1).
 *   - canonical_hostname (read-only por default; botao Editar libera).
 */
import { useEffect } from 'react'
import { ActionIcon, Badge, Card, Group, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core'
import { IconCheck, IconLock, IconLockOpen, IconX } from '@tabler/icons-react'
import { HostnamePreview } from './HostnamePreview'
import {
  buildCanonicalHostname,
  isValidSlug,
  type WizardStep1Data,
  type WizardStep2Data,
} from './wizardState'

interface Props {
  data: WizardStep2Data
  step1: WizardStep1Data
  onChange: (next: WizardStep2Data) => void
}

export function Step2Identity({ data, step1, onChange }: Props) {
  // Auto-derive slug do primeiro label do public_hostname caso vazio.
  // Convention regex do broker (PROTECTED_ENV_RE) extrai <app>.<env>.<platform>
  // — slug precisa bater com <app> pra resolver via fallback. Ex:
  //   public_hostname=portal.qa.onmidia.amazonasinovare.com.br → slug=portal
  // User pode editar livre (sem auto-derivacao depois do primeiro fill).
  useEffect(() => {
    if (data.slug) return
    if (!step1.public_hostname) return
    const firstLabel = step1.public_hostname.split('.')[0]?.toLowerCase()
    if (firstLabel && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(firstLabel)) {
      onChange({ ...data, slug: firstLabel })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step1.public_hostname])

  // Sempre que slug/tenant_slug/env mudarem, redireciona o canonical
  // automaticamente — exceto se usuario travou (canonical_locked=true).
  useEffect(() => {
    if (data.canonical_locked) return
    const next = buildCanonicalHostname(data.slug, step1.tenant_slug, step1.environment)
    if (next && next !== data.canonical_hostname) {
      onChange({ ...data, canonical_hostname: next })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.slug, step1.tenant_slug, step1.environment, data.canonical_locked])

  const slugValid = isValidSlug(data.slug)
  const slugError = data.slug && !slugValid
    ? 'Slug deve ser kebab-case lowercase (ex: portal-mdm), 2-64 chars'
    : null

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Title order={4}>Identidade do app</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Como o app vai aparecer no catálogo e no broker.
          </Text>
        </div>

        <TextInput
          label="Slug"
          placeholder="ex: portal"
          required
          value={data.slug}
          onChange={(e) =>
            onChange({ ...data, slug: e.currentTarget.value.trim().toLowerCase() })
          }
          error={slugError}
          rightSection={
            slugValid ? <IconCheck size={16} color="green" /> : data.slug ? <IconX size={16} color="red" /> : null
          }
          ff="JetBrains Mono, monospace"
          description="Identificador único (lowercase, dash). Usado em logs, audit, OIDC client_id."
        />

        <TextInput
          label="Display name"
          placeholder="ex: SafeCheck Portal HML"
          required
          value={data.display_name}
          onChange={(e) => onChange({ ...data, display_name: e.currentTarget.value })}
          description="Como aparece no catálogo de apps e no dashboard."
        />

        <div>
          <Text size="sm" fw={500}>Ambiente</Text>
          <Group
            gap={10}
            mt={4}
            p="sm"
            style={{
              background: 'var(--ai-bg-elevated)',
              borderRadius: 4,
              border: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            {step1.environment ? (
              <Badge color="blue" variant="light">{step1.environment}</Badge>
            ) : (
              <Badge color="gray" variant="light">indefinido</Badge>
            )}
            <Text size="xs" c="dimmed">
              derivado do tenant{' '}
              {step1.tenant_name ? <code>{step1.tenant_name}</code> : '(selecione no Step 1)'}
            </Text>
          </Group>
        </div>

        <div>
          <Group justify="space-between" align="flex-end" mb={4}>
            <Text size="sm" fw={500}>Hostname canonical (ZTNA)</Text>
            <Tooltip
              label={data.canonical_locked ? 'Travar (volta a derivar)' : 'Editar manualmente'}
            >
              <ActionIcon
                variant="subtle"
                onClick={() => onChange({ ...data, canonical_locked: !data.canonical_locked })}
                aria-label="Toggle canonical edit"
              >
                {data.canonical_locked ? <IconLockOpen size={16} /> : <IconLock size={16} />}
              </ActionIcon>
            </Tooltip>
          </Group>
          <TextInput
            value={data.canonical_hostname}
            onChange={(e) => onChange({ ...data, canonical_hostname: e.currentTarget.value })}
            readOnly={!data.canonical_locked}
            ff="JetBrains Mono, monospace"
            description="Formato <slug>--<tenant-slug>--<env>.zt.console.amazonasinovare.com.br"
          />
        </div>

        <HostnamePreview
          publicHostname={step1.public_hostname}
          canonicalHostname={data.canonical_hostname}
        />
      </Stack>
    </Card>
  )
}
